import React, { useEffect, useState } from "react";
import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import { SimpleCarousel } from "~/components/ui/simple-carousel";
import { useNavigationLoading } from "~/hooks/use-navigation-loading";
import { coachQueryOptions } from "~/lib/query-options";
import { createServerFn } from "@tanstack/react-start";
import { getHeader } from "@tanstack/react-start/server";
import { getCookieValue } from "~/lib/utils";
import { getDBWithInit } from "~/lib/db";
import { eq } from "drizzle-orm";
import { userPreferences } from "~/../drizzle/schema";
import { CoachCard } from "~/components/coach-card";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { ProfileSkeleton } from "~/components/ui/profile-skeleton";
import { CoachCardSkeleton } from "~/components/coach-card-skeleton";
import { Modal } from "~/components/ui/modal/modal";
import { AuthModal } from "~/components/auth-modal";
import { CoachLayout } from "~/components/layouts/coach-layout";
import { CoachProfileLayout } from "~/components/layouts/coach-profile-layout";
import type { CoachMatchPreferences } from "~/lib/matching";
import type { MembershipPlan } from "~/lib/schemas";
import { createCoachMeta, createPersonStructuredData } from "~/lib/shared/meta";

interface CoachMatchData {
  coach: {
    id: string;
    username: string;
    fullName: string;
    profilePictureUrl?: string | null;
    membershipPlan: MembershipPlan;
  };
  profile?: {
    coachingTitle?: string | null;
    brandLogoUrl?: string | null;
    brandName?: string | null;
    title?: string | null;
  } | null;
  similarity?: number;
  expertise?: string;
}

// Server function to get user preferences from database
export const getUserPreferencesFromDB = createServerFn({
  method: "GET",
}).handler(async () => {
  try {
    const cookieHeader = getHeader("cookie") || "";
    const userId = getCookieValue(cookieHeader, "userId");

    if (!userId) {
      return null;
    }

    const db = await getDBWithInit();
    const preferences = await db.query.userPreferences.findFirst({
      where: eq(userPreferences.userId, userId),
    });

    return preferences;
  } catch {
    // Return null if preferences can't be loaded
    return null;
  }
});

export const Route = createFileRoute("/_public/discovery/$username")({
  loader: async ({ context, params }) => {
    try {
      const coach = await context.queryClient.ensureQueryData(
        coachQueryOptions.byUsername(params.username),
      );

      if (!coach) {
        throw redirect({ to: "/discovery/steps/1" });
      }

      // Try to preload matches from database if user has preferences
      let preloadedMatches: CoachMatchData[] = [];
      try {
        const dbPreferences = await getUserPreferencesFromDB();
        if (dbPreferences) {
          const { getCoachMatches } = await import("~/lib/matching");
          const matchResults = await getCoachMatches({
            data: {
              preferences: dbPreferences as CoachMatchPreferences,
              limit: 12,
              queryLimit: 20,
            },
          });

          preloadedMatches = matchResults.map((result) => ({
            coach: {
              id: result.coach.id,
              username: result.coach.username,
              fullName: result.coach.fullName,
              profilePictureUrl: result.coach.profilePictureUrl,
              membershipPlan: result.coach.membershipPlan,
            },
            profile: result.profile
              ? {
                  coachingTitle: result.profile.coachingTitle,
                  brandLogoUrl: result.profile.brandLogoUrl,
                  brandName: result.profile.brandName,
                  title: result.profile.title,
                }
              : null,
            similarity: result.similarity,
            expertise: result.expertise,
          }));
        }
      } catch {
        // Failed to preload matches, continue without them
      }

      return {
        coach,
        preloadedMatches,
      };
    } catch {
      // Coach not found or other error, redirect to discovery
      throw redirect({ to: "/discovery/steps/1" });
    }
  },
  head: ({ loaderData, params }) => {
    const coach = loaderData?.coach;
    if (!coach) {
      return {
        title: "Coach Discovery | Dream Coach Match",
        meta: [
          {
            name: "description",
            content:
              "Discover your ideal coach through our personalized matching system",
          },
        ],
      };
    }

    const description = `Explore ${coach.fullName}'s coaching profile through our discovery system. ${coach.profile?.description ? coach.profile.description.slice(0, 160) + "..." : "Discover how this coach can help you achieve your goals."}`;
    const imageUrl = coach.profile?.heroImageSrc || coach.profilePictureUrl;
    const currentUrl = `https://dreamcoachmatch.com/discovery/${params.username}`;

    const metaTags = createCoachMeta(coach.fullName, {
      description,
      ogDescription: description,
      ogImage: imageUrl || undefined,
      ogUrl: currentUrl,
      ogType: "profile",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      twitterImage: imageUrl || undefined,
      canonical: currentUrl,
      author: coach.fullName,
      keywords: [
        "coach discovery",
        "coaching",
        "personalized match",
        coach.profile?.coachingTitle || "life coach",
        "coaching consultation",
      ],
    });

    // Add structured data for the person/coach
    const structuredData = createPersonStructuredData({
      name: coach.fullName,
      jobTitle: coach.profile?.coachingTitle || "Life Coach",
      image: imageUrl || undefined,
      url: currentUrl,
      description: coach.profile?.description || description,
    });

    return {
      ...metaTags,
      title: `Discover ${coach.fullName} - Coach Discovery | Dream Coach Match`,
      script: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  component: DiscoveryLayout,
});

function DiscoveryLayout() {
  const { username } = Route.useParams();
  const { coach, preloadedMatches } = Route.useLoaderData();
  const navigate = useNavigate();
  const clerk = useClerk();
  const auth = useAuth();
  const isLoading = useNavigationLoading();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [matches, setMatches] = useState<CoachMatchData[]>(
    preloadedMatches || [],
  );
  const [isLoadingMatches, setIsLoadingMatches] = useState(
    !preloadedMatches || 0 === preloadedMatches.length,
  );

  const { data: queryCoach } = useQuery({
    ...coachQueryOptions.byUsername(username),
    initialData: coach,
  });

  // Load matches client-side from localStorage only if we don't have preloaded matches
  useEffect(() => {
    // If we already have preloaded matches, skip localStorage loading
    if (preloadedMatches && 0 < preloadedMatches.length) {
      setIsLoadingMatches(false);
      return;
    }

    const loadMatches = async () => {
      try {
        const storedPreferences = localStorage.getItem("userPreferences");

        if (!storedPreferences) {
          // No preferences found - redirect to discovery flow
          navigate({ to: "/discovery/steps", replace: true });
          return;
        }

        const preferences = JSON.parse(storedPreferences);

        const { getCoachMatches } = await import("~/lib/matching");
        const matchResults = await getCoachMatches({
          data: {
            preferences,
            limit: 12,
            queryLimit: 20,
          },
        });

        const formattedMatches = matchResults.map((result) => ({
          coach: {
            id: result.coach.id,
            username: result.coach.username,
            fullName: result.coach.fullName,
            profilePictureUrl: result.coach.profilePictureUrl,
            membershipPlan: result.coach.membershipPlan,
          },
          profile: result.profile
            ? {
                coachingTitle: result.profile.coachingTitle,
                brandLogoUrl: result.profile.brandLogoUrl,
                brandName: result.profile.brandName,
                title: result.profile.title,
              }
            : null,
          similarity: result.similarity,
          expertise: result.expertise,
        }));
        setMatches(formattedMatches);
      } catch {
        // Failed to load matches, loading state will be reset in finally
      } finally {
        setIsLoadingMatches(false);
      }
    };

    loadMatches();
  }, [username, navigate, preloadedMatches]);

  const renderItem = (
    matchData: CoachMatchData,
    index: number,
    isActive: boolean,
  ) => {
    const isAuthenticated = Boolean(auth.isSignedIn || clerk?.isSignedIn);
    const isEnabled = isAuthenticated || [2, 3].includes(index);

    return (
      <CoachCard
        coachData={{
          coach: matchData.coach,
          profile: matchData.profile || null,
          similarity: matchData.similarity,
          expertise: matchData.expertise,
        }}
        isActive={isActive}
        isEnabled={isEnabled}
        href={`/discovery/${matchData.coach.username}`}
        onLockClick={() => setShowSignupModal(true)}
        className="h-96 overflow-hidden"
      />
    );
  };

  const renderSkeleton = (_: unknown, i: number) => (
    <CoachCardSkeleton className="animate-in fade-in h-96" seed={i} />
  );

  if (!queryCoach) {
    return <div>No coach data found</div>;
  }

  return (
    <CoachLayout username={username} coach={queryCoach}>
      <div className="mx-auto my-4 flex w-full max-w-screen-xl flex-col overflow-visible px-4 md:mt-8">
        <Breadcrumb
          className="mb-6"
          items={[
            { label: "Home", href: "/" },
            { label: "Coach Discovery", href: "/discovery" },
          ]}
        />
        <h2 className="font-heading mb-6 text-3xl font-medium tracking-tight">
          Your Recommended Coaches
        </h2>
        {!isLoadingMatches && 0 < matches.length ? (
          <SimpleCarousel
            items={matches}
            itemWidth={280}
            selectedIndex={(() => {
              const currentCoachIndex = matches.findIndex(
                (match) => match.coach.username === queryCoach?.username,
              );
              // If current coach is found, show it
              if (-1 !== currentCoachIndex) {
                return currentCoachIndex;
              }
              // Default to index 2 if coach not found or for non-auth users
              return auth.isSignedIn || clerk?.isSignedIn ? 0 : 2;
            })()}
            renderItem={renderItem}
          />
        ) : (
          <SimpleCarousel
            items={Array.from({ length: 10 }).fill(null)}
            itemWidth={280}
            selectedIndex={2}
            renderItem={renderSkeleton}
          />
        )}
      </div>

      <CoachProfileLayout coach={queryCoach} routePath="/discovery">
        {isLoading ? <ProfileSkeleton /> : <Outlet />}
      </CoachProfileLayout>

      <Modal.Root open={showSignupModal} onOpenChange={setShowSignupModal}>
        <Modal.Panel size="2xl" className="max-h-[90vh] overflow-y-auto">
          <AuthModal
            isModalView={true}
            initialMode="signup"
            onSuccess={() => {
              setShowSignupModal(false);
            }}
          />
        </Modal.Panel>
      </Modal.Root>
    </CoachLayout>
  );
}
